import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getLanguageValue } from 'locales/i18n';
import { PAGE_TYPE } from 'enums';
import { getProfileImageUrl } from 'helpers/Image.helper';
import { getFormattedDateValue } from 'helpers/Datetime.helper';
import { ISajuAnalysis } from 'api/student/v2/event.api';
import DetailSkeleton from './DetailSkeleton';

interface FortuneTellingDetailProps {
    isLoading: boolean;
    isLoadingAnalysis: boolean;
    profileImage: any;
    birthday: string | null;
    gender: string | null;
    hasAnalysis: boolean;
    sajuAnalysis: ISajuAnalysis | null;
    onViewFortuneTelling: () => void;
}

const FortuneTellingDetailComponent: React.FC<FortuneTellingDetailProps> = ({
    isLoading,
    isLoadingAnalysis,
    profileImage,
    birthday,
    gender,
    hasAnalysis,
    sajuAnalysis,
    onViewFortuneTelling
}) => {
    const navigate = useNavigate();


    return (
        <main className="bg-gradient-to-br from-pink-50 via-purple-50 to-pink-100 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6">
                <div className="max-w-2xl mx-auto">
                    {/* 헤더 영역 */}
                    <div className="flex items-center gap-4 mb-6">
                        <button 
                            onClick={() => navigate('/student/hub/event')} 
                            className="text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtEvent2026Title")}
                        </h1>
                    </div>

                    {isLoading ? (
                        <DetailSkeleton />
                    ) : hasAnalysis && sajuAnalysis ? (
                        // 사주 관상 정보가 있는 화면
                        <div className="space-y-4">
                            {/* 프로필 카드 */}
                            <div className="flex justify-center mb-4">
                                <div className="relative bg-gradient-to-br from-pink-100 via-purple-50 to-pink-100 rounded-3xl shadow-lg border-4 border-pink-300 overflow-hidden inline-block w-full max-w-md">
                                    {/* 프로필 사진 영역 - 상단 전체 */}
                                    <div className="relative w-full h-48 overflow-hidden">
                                        {profileImage ? (
                                            <img
                                                src={getProfileImageUrl(profileImage, 300)}
                                                alt={getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtProfilePhoto")}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = './imgs/dummy/basic-img.png';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-200 to-purple-200">
                                                <svg className="w-20 h-20 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                        )}
                                        {/* 장식 요소 */}
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-300/30 to-pink-300/30 rounded-full blur-2xl"></div>
                                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-300/30 to-blue-300/30 rounded-full blur-xl"></div>
                                    </div>
                                    
                                    {/* 정보 영역 */}
                                    <div className="relative p-6">
                                        <div className="text-center space-y-3">
                                            {birthday && (
                                                <p className="text-base font-semibold text-gray-800">
                                                    {getFormattedDateValue(birthday, getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtDateFormatYearMonthDay"))}
                                                </p>
                                            )}
                                            {gender && (
                                                <p className="text-base font-semibold text-gray-800">{gender}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 얼굴 인상 */}
                            {sajuAnalysis.face_impression && (
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-purple-100">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 text-center">
                                        ✨ {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingFaceImpression")}
                                    </h3>
                                    <p className="text-base text-gray-700 leading-relaxed">
                                        {sajuAnalysis.face_impression}
                                    </p>
                                </div>
                            )}

                            {/* 매력 포인트 */}
                            {sajuAnalysis.attractive_points && (
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-pink-100">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 text-center">
                                        💫 {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingAttractivePoints")}
                                    </h3>
                                    <p className="text-base text-gray-700 leading-relaxed">
                                        {sajuAnalysis.attractive_points}
                                    </p>
                                </div>
                            )}

                            {/* 적합한 포지션 */}
                            {sajuAnalysis.suitable_position && (
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-blue-100">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 text-center">
                                        🎯 {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingSuitablePosition")}
                                    </h3>
                                    <p className="text-base text-gray-700 leading-relaxed">
                                        {sajuAnalysis.suitable_position}
                                    </p>
                                </div>
                            )}

                            {/* 전체 흐름 */}
                            {sajuAnalysis.overall_flow && (
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-purple-100">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 text-center">
                                        🌊 {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingOverallFlow")}
                                    </h3>
                                    <p className="text-base text-gray-700 leading-relaxed">
                                        {sajuAnalysis.overall_flow}
                                    </p>
                                </div>
                            )}

                            {/* 2026년 운세 */}
                            {sajuAnalysis.year_2026_flow && (
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-pink-100">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 text-center">
                                        🔮 {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingYear2026Flow")}
                                    </h3>
                                    <p className="text-base text-gray-700 leading-relaxed">
                                        {sajuAnalysis.year_2026_flow}
                                    </p>
                                </div>
                            )}

                            {/* 추천 방향 */}
                            {sajuAnalysis.recommended_direction && (
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-blue-100">
                                    <h3 className="text-base font-bold text-gray-900 mb-4 text-center">
                                        ⭐ {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingRecommendedDirection")}
                                    </h3>
                                    <p className="text-base text-gray-700 leading-relaxed">
                                        {sajuAnalysis.recommended_direction}
                                    </p>
                                </div>
                            )}

                            {/* 하단 안내 문구 */}
                            <p className="text-sm text-gray-500 text-center pt-4">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingFooter")}😁
                            </p>
                        </div>
                    ) : (
                        // 사주 관상 정보가 없는 화면
                        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border-2 border-pink-200">
                            {/* 프로필 카드 */}
                            <div className="flex justify-center mb-8">
                                <div className="relative bg-gradient-to-br from-pink-100 via-purple-50 to-pink-100 rounded-3xl shadow-lg border-4 border-pink-300 overflow-hidden inline-block w-full max-w-md">
                                    {/* 프로필 사진 영역 - 상단 전체 */}
                                    <div className="relative w-full h-48 overflow-hidden">
                                        {profileImage ? (
                                            <img
                                                src={getProfileImageUrl(profileImage, 300)}
                                                alt={getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtProfilePhoto")}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = './imgs/dummy/basic-img.png';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-200 to-purple-200">
                                                <svg className="w-20 h-20 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                        )}
                                        {/* 장식 요소 */}
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-300/30 to-pink-300/30 rounded-full blur-2xl"></div>
                                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-300/30 to-blue-300/30 rounded-full blur-xl"></div>
                                    </div>
                                    
                                    {/* 정보 영역 */}
                                    <div className="relative p-6">
                                        <div className="text-center space-y-3">
                                            {birthday && (
                                                <p className="text-base font-semibold text-gray-800">
                                                    {getFormattedDateValue(birthday, getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtDateFormatYearMonthDay"))}
                                                </p>
                                            )}
                                            {gender && (
                                                <p className="text-base font-semibold text-gray-800">{gender}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 안내 문구 */}
                            <p className="text-xs text-gray-500 mb-4 text-center">
                                * {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingInfoUpdate")}{' '}
                                <Link 
                                    to="/student/profile/public" 
                                    className="underline text-gray-700 hover:text-gray-900"
                                >
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingInfoUpdateLink")}
                                </Link>
                                {' '}{getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingInfoUpdateDesc")}
                            </p>

                            {/* 관상 사주보기 버튼 */}
                            <div className="mb-6">
                                <button
                                    onClick={onViewFortuneTelling}
                                    disabled={isLoadingAnalysis}
                                    className="w-full py-4 px-6 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-bold rounded-2xl transition-all text-base shadow-lg transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
                                >
                                    {isLoadingAnalysis && (
                                        <span className="absolute inset-0 flex items-center justify-center">
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </span>
                                    )}
                                    <span className={isLoadingAnalysis ? 'opacity-0' : ''}>
                                        {isLoadingAnalysis 
                                            ? `✨ ${getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingAnalyzing")} ✨` 
                                            : `🔮 ${getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingView")} 🔮`
                                        }
                                    </span>
                                </button>
                            </div>

                            {/* 안내 문구 */}
                            <div className="bg-pink-50 border-2 border-pink-200 rounded-2xl p-5 space-y-3">
                                <h3 className="text-base font-bold text-gray-900 text-center">
                                    ✨ {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeTitle")}
                                </h3>
                                <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                                    <p>
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeDesc1")}<br />
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeDesc2")}{' '}
                                        <strong>{getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeDesc3")}</strong><br />
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeDesc4")}
                                    </p>
                                    <div className="space-y-2">
                                        <p className="flex items-start gap-2">
                                            <span className="text-pink-500">✔</span>
                                            <span>{getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeCheck1")}</span>
                                        </p>
                                        <p className="flex items-start gap-2">
                                            <span className="text-pink-500">✔</span>
                                            <span>{getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeCheck2")}</span>
                                        </p>
                                        <p className="flex items-start gap-2">
                                            <span className="text-pink-500">✔</span>
                                            <span>{getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeCheck3")}</span>
                                        </p>
                                    </div>
                                    <p className="text-center pt-2">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSajuFortuneTellingNoticeFooter")} 😊
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default FortuneTellingDetailComponent;

